import { BaseAdapter } from "./base-adapter";
import {
  type TEvent,
  EventType,
  type IPageViewEvent,
  type IButtonClickEvent,
  type IFormSubmissionEvent,
  type IProductViewEvent,
  type IAddToCartEvent,
  type IRemoveFromCartEvent,
  type ICheckoutStartedEvent,
  type ICheckoutCompletedEvent,
  type ISearchEvent,
  type IUserSignupEvent,
  type IUserLoginEvent,
  type ICustomEvent,
} from "../types";
import { EventNames, DEFAULT_CURRENCY } from "../constants";

import moengage from "@moengage/web-sdk";

/**
 * Configuration for the MoenGage adapter
 */
export interface MoengageAdapterConfig {
  /**
   * MoenGage App ID (required for SDK initialization)
   */
  appId: string;
  /**
   * Whether to enable user identification
   */
  enableUserIdentification?: boolean;
  /**
   * Debug mode for MoenGage SDK
   */
  debug?: boolean;
  /**
   * Data center region (default: 'dc_01')
   */
  region?: string;
}

/**
 * MoenGage tracking adapter using the official Web SDK
 */
export class MoengageAdapter extends BaseAdapter {
  public readonly name = "MoEngage";
  private sdk: any = null;

  constructor(config: MoengageAdapterConfig) {
    super(config);
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize the MoenGage adapter using the Web SDK
   */
  public async initialize(): Promise<void> {
    if (this.initialized) {
      this.logger.debug("Already initialized");
      return;
    }

    // Skip initialization if running on server
    if (typeof window === "undefined") {
      this.logger.debug("Skipping initialization on server");
      return;
    }

    try {
      const appId = this.getConfig<string>("appId");
      if (!appId) {
        this.logger.warn("App ID is required for initialization");
        return;
      }

      // Initialize MoenGage SDK
      const region = this.getConfig<string>("region", "dc_01");
      const config = {
        app_id: appId,
        debug_logs: this.getConfig<boolean>("debug", false) ? 1 : 0,
        cluster: region,
      };

      this.logger.info("Initializing with config", {
        region,
        debug: this.getConfig<boolean>("debug", false),
        appId: "***", // Hide sensitive data
      });

      // Initialize MoEngage SDK
      moengage.initialize(config);

      // After initialization, the SDK becomes available on window.Moengage
      this.sdk = (window as any).Moengage;

      this.logger.info("Successfully initialized with SDK");
      this.initialized = true;
    } catch (error) {
      this.logger.error("Failed to initialize", error as Error);
    }
  }

  /**
   * Track an event with MoenGage SDK
   * @param event The event to track
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined" || !this.sdk) {
      this.logger.warn(
        "Cannot track event, adapter not initialized or SDK not available",
        {
          eventType: event.type,
          initialized: this.initialized,
          hasWindow: typeof window !== "undefined",
          hasSdk: !!this.sdk,
        }
      );
      return;
    }

    this.logger.debug("Tracking event", { eventType: event.type });

    switch (event.type) {
      case EventType.PAGE_VIEW:
        this.trackPageView(event as IPageViewEvent);
        break;
      case EventType.BUTTON_CLICK:
        this.trackButtonClick(event as IButtonClickEvent);
        break;
      case EventType.FORM_SUBMISSION:
        this.trackFormSubmission(event as IFormSubmissionEvent);
        break;
      case EventType.PRODUCT_VIEW:
        this.trackProductView(event as IProductViewEvent);
        break;
      case EventType.ADD_TO_CART:
        this.trackAddToCart(event as IAddToCartEvent);
        break;
      case EventType.REMOVE_FROM_CART:
        this.trackRemoveFromCart(event as IRemoveFromCartEvent);
        break;
      case EventType.CHECKOUT_STARTED:
        this.trackCheckoutStarted(event as ICheckoutStartedEvent);
        break;
      case EventType.CHECKOUT_COMPLETED:
        this.trackCheckoutCompleted(event as ICheckoutCompletedEvent);
        break;
      case EventType.SEARCH:
        this.trackSearch(event as ISearchEvent);
        break;
      case EventType.USER_SIGNUP:
        this.trackUserSignup(event as IUserSignupEvent);
        break;
      case EventType.USER_LOGIN:
        this.trackUserLogin(event as IUserLoginEvent);
        break;
      case EventType.CUSTOM:
        this.trackCustomEvent(event as ICustomEvent);
        break;
      default:
        // For other events, track as custom events
        this.sdk.track_event(event.type, this.eventToParams(event));
        break;
    }
  }

  /**
   * Track a page view event
   * @param event The page view event
   */
  private trackPageView(event: IPageViewEvent): void {
    if (!this.sdk?.track_event) {
      this.logger.warn("track_event not available for PageView event");
      return;
    }

    const eventData = {
      page_path: event.path,
      page_title: event.title,
      page_url: window.location.href,
      referrer: event.referrer || document.referrer,
    };

    this.sdk.track_event(EventNames.PAGE_VIEWED, eventData);
    this.logger.debug("PageView event tracked", { eventData });
  }

  /**
   * Track a button click event
   * @param event The button click event
   */
  private trackButtonClick(event: IButtonClickEvent): void {
    this.sdk?.track_event(EventNames.BUTTON_CLICKED, {
      button_id: event.buttonId,
      button_text: event.buttonText,
      location: event.location,
    });
  }

  /**
   * Track a form submission event
   * @param event The form submission event
   */
  private trackFormSubmission(event: IFormSubmissionEvent): void {
    this.sdk?.track_event(EventNames.FORM_SUBMITTED, {
      form_id: event.formId,
      form_name: event.formName,
      success: event.success,
    });
  }

  /**
   * Track a product view event
   * @param event The product view event
   */
  private trackProductView(event: IProductViewEvent): void {
    this.sdk?.track_event(EventNames.PRODUCT_VIEWED, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      category: event.category,
    });
  }

  /**
   * Track an add to cart event
   * @param event The add to cart event
   */
  private trackAddToCart(event: IAddToCartEvent): void {
    const quantity = event.quantity ?? 1;
    this.sdk?.track_event(EventNames.ADDED_TO_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: quantity,
      variant: event.variant,
      total_value: event.price * quantity,
    });
  }

  /**
   * Track a remove from cart event
   * @param event The remove from cart event
   */
  private trackRemoveFromCart(event: IRemoveFromCartEvent): void {
    this.sdk?.track_event(EventNames.REMOVED_FROM_CART, {
      product_id: event.productId,
      product_name: event.productName,
      price: event.price,
      currency: event.currency || DEFAULT_CURRENCY,
      quantity: event.quantity,
      variant: event.variant,
      total_value: event.price * event.quantity,
    });
  }

  /**
   * Track a checkout started event
   * @param event The checkout started event
   */
  private trackCheckoutStarted(event: ICheckoutStartedEvent): void {
    this.sdk?.track_event(EventNames.CHECKOUT_STARTED, {
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a checkout completed event
   * @param event The checkout completed event
   */
  private trackCheckoutCompleted(event: ICheckoutCompletedEvent): void {
    this.sdk?.track_event(EventNames.PURCHASE_COMPLETED, {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency || DEFAULT_CURRENCY,
      item_count: event.itemCount,
      items: event.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        price: item.price,
        quantity: item.quantity,
        variant: item.variant,
      })),
    });
  }

  /**
   * Track a search event
   * @param event The search event
   */
  private trackSearch(event: ISearchEvent): void {
    this.sdk?.track_event(EventNames.SEARCH, {
      search_term: event.searchTerm,
      results_count: event.resultsCount,
    });
  }

  /**
   * Track a user signup event
   * @param event The user signup event
   */
  private trackUserSignup(event: IUserSignupEvent): void {
    this.sdk?.track_event(EventNames.USER_SIGNED_UP, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a user login event
   * @param event The user login event
   */
  private trackUserLogin(event: IUserLoginEvent): void {
    this.sdk?.track_event(EventNames.USER_LOGGED_IN, {
      user_id: event.userId,
      method: event.method,
      success: event.success,
    });

    // Add user attributes if user ID is available and user identification is enabled
    if (
      event.userId &&
      this.getConfig<boolean>("enableUserIdentification", true)
    ) {
      this.sdk?.add_unique_user_id(event.userId);
    }
  }

  /**
   * Track a custom event
   * @param event The custom event
   */
  private trackCustomEvent(event: ICustomEvent): void {
    this.sdk?.track_event(event.name, event.properties);
  }

  /**
   * Convert an event to MoenGage parameters
   * @param event The event to convert
   */
  private eventToParams(event: TEvent): Record<string, any> {
    // Remove type and timestamp from the event
    const { type, timestamp, ...params } = event;
    return params;
  }

  /**
   * Set user attributes in MoenGage
   * @param attributes User attributes to set
   */
  public setUserAttributes(attributes: Record<string, any>): void {
    if (!this.initialized || typeof window === "undefined" || !this.sdk) {
      this.logger.warn("Cannot set user attributes, adapter not initialized");
      return;
    }

    this.logger.debug("Setting user attributes", {
      attributeKeys: Object.keys(attributes),
    });

    // Set user attributes
    for (const [key, value] of Object.entries(attributes)) {
      switch (key) {
        case "firstName":
          this.sdk?.add_first_name(value as string);
          break;
        case "lastName":
          this.sdk?.add_last_name(value as string);
          break;
        case "email":
          this.sdk?.add_email(value as string);
          break;
        case "mobile":
          this.sdk?.add_mobile(value as string);
          break;
        case "username":
          this.sdk?.add_user_name(value as string);
          break;
        case "gender":
          this.sdk?.add_gender(value as string);
          break;
        case "birthday":
          this.sdk?.add_birthday(value as Date);
          break;
        case "userId":
          this.sdk?.add_unique_user_id(value as string);
          break;
        default:
          this.sdk?.add_user_attribute(key, value);
          break;
      }
    }
  }
}
